/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useContext, useState } from 'react';
import {
  View,
  TouchableHighlight,
  StyleSheet,
  Dimensions,
  ActivityIndicator,
} from 'react-native';
import { SvgXml } from 'react-native-svg';
import DyteText from './DyteText';
import DyteImageMessage from './DyteImageMessage';
import { defaultIconPack, IconPack } from '../lib/icons';
import { DyteI18n, useLanguage } from '../lib/lang';
import { downloadFile } from '../utils/file';
import { ImageMessage } from '@dytesdk/web-core';
import { DyteUIContext } from '../lib/context-provider/ui-context';
import DyteSpinner from './DyteSpinner';

export default function ({
  image,
  size,
  iconPack = defaultIconPack,
  t,
  _id,
}: {
  image: any;
  size?: 'lg' | 'md' | 'sm' | 'xl';
  iconPack?: IconPack;
  t?: DyteI18n;
  _id?: string | number;
}) {
  const [downloading, setDownloading] = useState(false);
  const defaultLang = useLanguage();
  const { storeStates, setStates } = useContext(DyteUIContext);
  const { colors } = storeStates.designSystem;
  t = t ?? defaultLang;
  iconPack = iconPack ?? defaultIconPack;
  const downloadImage = async () => {
    downloadFile((image as ImageMessage).link, {
      name:
        'DyteChatImage' + Math.floor(Math.random() * 1000).toString() + '.png',
    }).then(() => setDownloading(false));
    setDownloading(true);
  };
  const toggleFullscreen = () => {
    setStates({
      ...storeStates,
      fullscreenImageID: !storeStates.activeFullscreen ? _id : '',
      activeFullscreen: !storeStates.activeFullscreen,
    });
  };
  const styles = StyleSheet.create({
    viewerContainer: {
      padding: 8,
      borderRadius: 4,
      backgroundColor: colors.background[800],
    },
    controlStrip: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 8,
    },
    infoContainer: {
      flexDirection: 'row',
    },
    infoText: {
      fontSize: 14,
    },
    usernameText: {
      fontSize: 14,
      fontWeight: 'bold',
    },
    buttonContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
    },
    fullScreenIcon: {
      padding: 8,
      borderRadius: 4,
      margin: 4,
      backgroundColor: colors.background[700],
    },
    downloadIcon: {
      alignItems: 'center',
      justifyContent: 'space-evenly',
      flexDirection: 'row',
      backgroundColor: colors.brand[500],
      borderRadius: 4,
      padding: 8,
    },
    downloadIconGroup: {
      flexDirection: 'row',
    },
    downloadIconContainer: {
      alignItems: 'center',
      justifyContent: 'center',
      marginRight: 4,
    },
    textContainer: {
      justifyContent: 'center',
    },
    text: {
      fontSize: 12,
    },
  });
  const { width, height } = Dimensions.get('screen');
  if (storeStates.activeFullscreen) {
    return (
      <View
        // eslint-disable-next-line react-native/no-inline-styles
        style={{
          ...styles.viewerContainer,
          marginVertical: '15%',
          width: width * 0.8,
          height: height * 0.6,
        }}
      >
        <View style={styles.controlStrip}>
          <View style={styles.infoContainer}>
            <DyteText style={styles.infoText}>Shared by </DyteText>
            <DyteText style={styles.usernameText}>
              {image !== undefined ? image.user.name : 'Participant'}
            </DyteText>
          </View>
          <View style={styles.buttonContainer}>
            <TouchableHighlight
              underlayColor={colors.background[800]}
              style={styles.fullScreenIcon}
              onPress={() => toggleFullscreen()}
            >
              <SvgXml
                xml={iconPack.full_screen_minimize.replace(
                  'currentColor',
                  colors.text
                )}
                height={14}
                width={14}
              />
            </TouchableHighlight>
            <TouchableHighlight
              underlayColor={colors.background[800]}
              style={styles.downloadIcon}
              onPress={downloadImage}
            >
              <View style={styles.downloadIconGroup}>
                <View style={styles.downloadIconContainer}>
                  {downloading && (
                    <View style={styles.textContainer}>
                      <DyteSpinner />
                    </View>
                  )}
                  {!downloading && (
                    <SvgXml
                      xml={iconPack.download.replace(
                        'currentColor',
                        colors.text
                      )}
                      height={12}
                      width={12}
                    />
                  )}
                </View>
                <View style={styles.textContainer}>
                  <DyteText style={styles.text}>Download</DyteText>
                </View>
              </View>
            </TouchableHighlight>
          </View>
        </View>
        <DyteImageMessage message={image} />
      </View>
    );
  } else {
    return (
      <View style={styles.viewerContainer}>
        <View style={styles.controlStrip}>
          <View style={styles.infoContainer}>
            <DyteText style={styles.infoText}>Shared by </DyteText>
            <DyteText style={styles.usernameText}>
              {image !== undefined ? image.user.name : 'Participant'}
            </DyteText>
          </View>
          <View style={styles.buttonContainer}>
            <TouchableHighlight
              underlayColor={colors.background[800]}
              style={styles.fullScreenIcon}
              onPress={() => toggleFullscreen()}
            >
              <SvgXml
                xml={iconPack.full_screen_maximize.replace(
                  'currentColor',
                  colors.text
                )}
                height={14}
                width={14}
              />
            </TouchableHighlight>
            <TouchableHighlight
              underlayColor={colors.background[800]}
              style={styles.downloadIcon}
              onPress={downloadImage}
            >
              <View style={styles.downloadIconGroup}>
                <View style={styles.downloadIconContainer}>
                  {downloading && (
                    <View style={styles.textContainer}>
                      <DyteSpinner />
                    </View>
                  )}
                  {!downloading && (
                    <SvgXml
                      xml={iconPack.download.replace(
                        'currentColor',
                        colors.text
                      )}
                      height={12}
                      width={12}
                    />
                  )}
                </View>
                <View style={styles.textContainer}>
                  <DyteText style={styles.text}>Download</DyteText>
                </View>
              </View>
            </TouchableHighlight>
          </View>
        </View>
        <DyteImageMessage message={image} />
      </View>
    );
  }
}
